import React, { useMemo, useState } from 'react';

import {
  Box,
  Typography,
  IconButton,
  Alert,
  List,
  FormControlLabel,
} from '@mui/material';

import { useFlagCodeMutation } from '../../../common/apis/FlorenceApi';
import { ReactComponent as ChevronDownIcon } from '../../../common/assets/icons/chevron_down_icon.svg';
import { SelectableCode, ICD10Code } from '../../../common/types/Codes';
import SnackbarUtils from '../../../common/utils/SnackbarUtilsConfigurator';
import ModifierPopupMenu from '../../screens/SearchResultsScreen/components/ModifierPopup/ModifierPopupMenu';
import MultiplierPopupMenu from '../../screens/SearchResultsScreen/components/MultiplierPopupMenu';

import './SearchResultCell.scss';
import IOSSwitch from 'src/florence/screens/TFactorScreen/IOSSwitch';

import { CodeExtraInfo } from './components/CodeExtraInfo';
import { FlagButton } from './components/FlagButton';
import { ModifiersButton } from './components/ModifiersButton';
import { UnitsButton } from './components/Unitsbutton';
import { UseButton } from './components/UseButton';
import SearchICD10Codes from './SearchICD10Codes';

type Props = {
  code: SelectableCode;
  selectCode: (code: SelectableCode) => void;
  selectMultiplier: (code: SelectableCode, multiplier: number) => void;
  selectModifiers: (code: SelectableCode, modifierCodes: string[]) => void;
  updateICD10?: (code: SelectableCode, icd10: ICD10Code[]) => void;
  flagCode: (code: SelectableCode) => void;
  searchType: 'text' | 'scan';
  sheetId?: number;
  isClinicNote?: boolean;
  isSmartLaunch?: boolean;
  existsInFeeSchedule?: boolean;
  hasMultiplePrimaryCodes?: boolean;
  suggestedICD10?: ICD10Code[];
};

const SearchResultCell: React.FC<Props> = ({
  code,
  selectCode,
  selectMultiplier,
  selectModifiers,
  flagCode,
  sheetId,
  searchType,
  isClinicNote = false,
  isSmartLaunch = false,
  existsInFeeSchedule = false,
  hasMultiplePrimaryCodes = false,
  updateICD10 = (code, icd10) => null,
  suggestedICD10 = [],
}) => {
  const [flagCodeRequest] = useFlagCodeMutation();
  const [isOpen, setIsOpen] = useState(false);
  const [isModifierPopupOpen, setIsModifierPopupOpen] = useState(false);
  const [loading, setIsLoading] = useState(false);

  const handleRotate = () => setIsOpen(!isOpen);

  const rotate = isOpen ? 'rotate(180deg)' : 'rotate(0)';

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const didTapFlagCode = async () => {
    if (code.selected && sheetId) {
      flagCode(code);
      return;
    }

    // Send flag request for the current search result
    setIsLoading(true);
    flagCode(code);
    await flagCodeRequest({
      code: code,
      sheetId: sheetId,
      searchResultId: code.searchResultId,
    });
    setIsLoading(false);
    try {
      SnackbarUtils.toast(
        `Code ${code.isFlagged ? 'Unflagged' : 'Flagged'}`,
        'success'
      );
    } catch (error: any) {
      if (error.message) {
        SnackbarUtils.error(error.message);
      }
    }
  };

  const handleClickUnits = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseUnits = () => {
    setAnchorEl(null);
  };

  const handleSelectedUnit = (item: number) => {
    setAnchorEl(null);
    selectMultiplier(code, item);
  };

  const onConfirmModifiers = (
    code: SelectableCode,
    modifierCodes: string[]
  ) => {
    setIsModifierPopupOpen(false);
    selectModifiers(code, modifierCodes);
  };

  const switchICD10Code = React.useCallback(
    (event, icd10Code: ICD10Code) => {
      if (!code.icd10) return;

      const newICD10Codes: ICD10Code[] = code.icd10?.map((currentCode) => {
        if (currentCode.code === icd10Code.code) {
          return {
            ...currentCode,
            selected: event.target.checked,
          };
        }

        return currentCode;
      });

      updateICD10(code, newICD10Codes);
    },
    [code, updateICD10]
  );

  const updateICD10Code = React.useCallback(
    (changedICD10Code: ICD10Code) => {
      const newICD10Codes: ICD10Code[] = code.icd10 ? [...code.icd10] : [];

      const index = newICD10Codes.findIndex(
        (currentCode) => currentCode.code === changedICD10Code.code
      );

      if (index === -1) {
        newICD10Codes.push(changedICD10Code);
      } else {
        newICD10Codes[index] = changedICD10Code;
      }

      updateICD10(code, newICD10Codes);
    },
    [code, updateICD10]
  );

  const icd10CodesComponent = useMemo(() => {
    return (
      <>
        <SearchICD10Codes
          selectedCodes={code.icd10?.filter((code) => code.selected) ?? []}
          updateICD10Code={updateICD10Code}
          suggestedICD10={suggestedICD10}
        />
        <List sx={{ padding: 0 }}>
          {code.icd10?.map((icd10Code, index) => (
            <Box
              key={icd10Code.code + index}
              sx={{
                borderRadius: '16px',
                backgroundColor: 'rgba(219, 236, 239, 0.50)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                padding: '16px',
                marginTop: '8px',
                marginBottom: '8px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  flexWrap: 'wrap',
                  gap: '8px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                  }}
                >
                  <Typography
                    color='darkBlue'
                    fontSize={16}
                    fontWeight={700}
                    sx={{
                      marginRight: '4px',
                    }}
                  >
                    {icd10Code.code}
                  </Typography>
                  {icd10Code.description && (
                    <Typography color='darkBlue' fontSize={16} fontWeight={500}>
                      - {icd10Code.description}
                    </Typography>
                  )}
                </Box>
                {isSmartLaunch && icd10Code.source && (
                  <Box
                    className={`code-source-bubble from-${icd10Code.source}`}
                  >
                    <Typography>
                      {icd10Code.source === 'athena' && 'Currently in Athena'}
                      {icd10Code.source === 'galeai' && 'Recommended by GaleAI'}
                      {icd10Code.source === 'confirmed' &&
                        'Confirmed by GaleAI'}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box>
                <FormControlLabel
                  style={{ marginRight: '0' }}
                  control={
                    <IOSSwitch
                      sx={{ m: 1 }}
                      checked={icd10Code.selected}
                      onChange={(event) => switchICD10Code(event, icd10Code)}
                    />
                  }
                  label=''
                />
              </Box>
            </Box>
          ))}
        </List>
      </>
    );
  }, [code.icd10, isSmartLaunch, switchICD10Code, updateICD10Code]);

  return (
    <Box
      sx={{
        position: 'relative',
      }}
      className='table-view-cell-container-white'
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap',
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {(code.work_rvu || code.facility_total) && (
            <IconButton
              style={{
                transform: rotate,
                transition: 'all 0.25s ease-in-out',
                alignSelf: 'flex-start',
              }}
              onClick={() => handleRotate()}
              className={`${isOpen ? 'icon-selected' : 'icon'}`}
            >
              <ChevronDownIcon />
            </IconButton>
          )}

          <Typography fontFamily='SFProDisplay-Bold' sx={{ marginLeft: '0px' }}>
            {code.code}
          </Typography>
          {isClinicNote && code.isPrimaryCode && (
            <Box className={`code-source-bubble`}>
              <Typography>E&M</Typography>
            </Box>
          )}
          {isClinicNote && !code.manuallyAdded && code.from !== 'user' && (
            <Box className={`code-source-bubble from-${code.from}`}>
              <Typography>
                {code.from === 'athena' && 'Currently in Athena'}
                {code.from === 'galeai' && 'Recommended by GaleAI'}
                {code.from === 'confirmed' && 'Confirmed by GaleAI'}
              </Typography>
            </Box>
          )}
        </Box>
        <Box className='wrapper-code-buttons'>
          {searchType === 'scan' && code.selected && (
            <>
              <ModifiersButton
                code={code}
                onClick={() => setIsModifierPopupOpen(true)}
              />
              <UnitsButton code={code} onClick={handleClickUnits} />
            </>
          )}
          {!code.manuallyAdded && !isSmartLaunch && (
            <FlagButton
              loading={loading}
              didTapFlagCode={didTapFlagCode}
              code={code}
            />
          )}
          <UseButton
            code={code}
            selectCode={selectCode}
            hasMultiplePrimaryCodes={hasMultiplePrimaryCodes}
          />
        </Box>
      </Box>

      <Typography className='code-description'>{code.description}</Typography>
      {isSmartLaunch && !existsInFeeSchedule && code.from === 'galeai' && (
        <Alert
          severity='error'
          color='error'
          variant='standard'
          sx={{
            borderRadius: 16,

            marginTop: '12px',
            marginBottom: '12px',
          }}
        >
          Add this code in Athena manually, it&apos;s not in the fee schedule.
        </Alert>
      )}

      {isClinicNote && (
        <Box mt='20px'>
          <Typography fontWeight={500} fontSize={16} color='darkBlue'>
            ICD-10 Codes:
          </Typography>
          {icd10CodesComponent}
        </Box>
      )}

      {isOpen && <CodeExtraInfo code={code} />}

      <MultiplierPopupMenu
        handleSelectedItem={(item) => handleSelectedUnit(item)}
        anchorEl={anchorEl}
        handleClose={handleCloseUnits}
        open={open}
        id={id}
      />
      <ModifierPopupMenu
        code={code}
        open={isModifierPopupOpen}
        onClose={() => setIsModifierPopupOpen(false)}
        onConfirm={(modifierCodes) => onConfirmModifiers(code, modifierCodes)}
      />
    </Box>
  );
};

export default SearchResultCell;
